// src/app/core/interceptors/msal-auth.interceptor.ts
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { SilentRequest, AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { isAuthPath, isPublicPath } from '../auth/entry-auth.guard';
import { Observable, from } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { CurrentUserStore } from '../session/current-user.store';

function isPreflight(req: HttpRequest<unknown>): boolean {
  return req.method.toUpperCase() === 'OPTIONS';
}

function startsWithApiBase(url: string, apiBase: string): boolean {
  try {
    const absolute = new URL(url, window.location.origin).toString();
    const base = apiBase.endsWith('/') ? apiBase : apiBase + '/';
    return absolute.startsWith(base);
  } catch {
    return url.startsWith(apiBase);
  }
}

export const authInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const apiBase = environment.apiConfig.protectedResourceBaseUrl;

  if (!startsWithApiBase(request.url, apiBase)) {
    return next(request);
  }

  // 2) Preflight + ścieżki jawnie wykluczone
  if (isPreflight(request) || isAuthPath(request.url) || isPublicPath(request.url)) {
    return next(request);
  }

  // 3) Jeśli Authorization już jest — nie nadpisuj
  if (request.headers.has('Authorization')) {
    return next(request);
  }

  const msal = inject(MsalService);
  const currentUserStore = inject(CurrentUserStore);

  // 4) Ustaw/wybierz konto
  let account = msal.instance.getActiveAccount();
  const accounts = msal.instance.getAllAccounts();
  if (!account && accounts.length > 0) {
    account = accounts[0];
    msal.instance.setActiveAccount(account);
  }
  if (!account) {
    return next(request);
  }

  // 5) acquireTokenSilent → Observable
  const silentReq: SilentRequest = {
    account,
    scopes: environment.apiConfig.scopes,
    forceRefresh: false
  };

  return from(msal.instance.acquireTokenSilent(silentReq)).pipe(
    mergeMap((result: AuthenticationResult) => {
      const authReq = request.clone({
        setHeaders: { Authorization: `Bearer ${result.accessToken}` }
      });
      return next(authReq);
    }),
    catchError((e) => {
      // Check if this is an HTTP error (from the backend), not an MSAL error
      // HTTP errors should be propagated, not retried
      if (e?.status !== undefined) {
        // This is an HTTP error response, not an MSAL error
        // Re-throw it so it can be handled by the calling code
        throw e;
      }

      // Token expired or interaction required - force logout and redirect to login
      if (e instanceof InteractionRequiredAuthError) {
        console.warn('MSAL: Session expired, redirecting to login...');
        currentUserStore.clear();
        msal.logoutRedirect({
          postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri
        });
        // Return empty observable since we're redirecting
        return new Observable<HttpEvent<unknown>>();
      }

      // For other MSAL errors (network issues, etc.), try without auth
      console.warn('MSAL: Token acquisition failed, trying request without auth', e);
      return next(request);
    })
  );
};
